/**
 * Emote shortcodes: turning `:hymy:` in a message into a picture of one.
 *
 * Every emote answers to a shortcode in each language, and every client knows
 * all of them. That is what makes a mixed-language room work: the message
 * travels as whatever its author typed, so a Finn's `:kippis:` and an English
 * speaker's `:cheers:` are different text on the wire and the same picture on
 * both their screens. Nothing is rewritten in transit, which also means the
 * stored transcript, the search in it and the plugin commands all keep working
 * on plain readable text.
 *
 * An unknown code is left exactly as it was typed. So the set can grow without
 * a server release, and an old client simply shows `:uusi:` as words rather
 * than breaking.
 */
import { EMOTES as SHEET_EMOTES, TILE, FRAMES } from './emotes.data.js';
import { PACK_EMOTES } from './emote-packs.data.js';
import { getLocale } from './i18n.js';

/*
 * Two kinds of emote, and everything below this line treats them the same.
 *
 * The hand-drawn set is rows of one sprite sheet: 16x16, three frames, one shared
 * palette, animated by a CSS rule - see assets/emotes/set.mjs. An imported pack
 * is finished artwork in its own files, at whatever size and however many frames
 * its author drew, so it is an <img> instead. Only emoteNode() knows the
 * difference; a shortcode, the picker and a message full of them do not.
 *
 * Sheet first, then the packs, which is the order the picker draws them in: the
 * ten that belong to this room's own set should not be buried in an imported
 * two dozen.
 */
const EMOTES = [...SHEET_EMOTES, ...PACK_EMOTES];

/** Every shortcode in every language, pointing at the emote it draws. */
const BY_CODE = new Map();
for (const emote of EMOTES) {
  for (const code of Object.values(emote.codes)) BY_CODE.set(code, emote);
}

/**
 * Candidate shortcodes. Digits are allowed, which would otherwise make a
 * timestamp like `13:41:` a candidate - harmless, because only codes that are
 * actually in the set above are ever replaced.
 */
const CODE = /:([a-z0-9]+):/g;

/** The emote a shortcode names, or undefined. */
export function emoteFor(code) {
  return BY_CODE.get(String(code ?? '').toLowerCase());
}

/** This reader's own shortcode for an emote - what the picker inserts. */
export function codeFor(emote, locale = getLocale()) {
  return emote.codes[locale] ?? emote.codes.en ?? Object.values(emote.codes)[0];
}

/**
 * Splits text into runs of `{ text }` and `{ emote }`.
 *
 * Runs are returned in order and nothing is lost, so the caller can rebuild the
 * original by concatenating the text and the shortcodes back together.
 */
export function splitEmotes(text) {
  const value = String(text ?? '');
  const runs = [];
  let last = 0;
  for (const match of value.matchAll(CODE)) {
    const emote = emoteFor(match[1]);
    if (!emote) continue;
    if (match.index > last) runs.push({ text: value.slice(last, match.index) });
    runs.push({ emote });
    last = match.index + match[0].length;
  }
  if (last < value.length) runs.push({ text: value.slice(last) });
  return runs;
}

/** True when a message is nothing but emotes, and so worth drawing large. */
export function onlyEmotes(runs) {
  const emotes = runs.filter((run) => run.emote);
  if (emotes.length === 0 || emotes.length > 6) return false;
  return runs.every((run) => run.emote || run.text.trim() === '');
}

/**
 * One emote, as an element.
 *
 * The picture comes from the sheet by way of the stylesheet: the row picks the
 * emote and a steps() animation walks the frames across it. `still` is for the
 * one on the composer's button, where an icon that never settles is a fidget.
 */
export function emoteNode(emote, { still = false } = {}) {
  // An imported pack's artwork is its own file, so it is simply shown. The alt
  // text is the shortcode, which is the only name it has - same as the sheet's
  // aria-label below, by way of the mechanism an <img> already has for it.
  if (emote.src) {
    const image = document.createElement('img');
    image.className = 'emote-image';
    image.src = emote.src;
    image.alt = `:${codeFor(emote)}:`;
    // The intrinsic size, so the line does not reflow when the file arrives. The
    // stylesheet sets the height it is actually drawn at and leaves width to
    // follow, which is what keeps the few non-square ones from being squashed.
    if (emote.width) image.width = emote.width;
    if (emote.height) image.height = emote.height;
    image.decoding = 'async';
    image.loading = 'lazy';
    return image;
  }

  const node = document.createElement('span');
  node.className = still || !emote.ms ? 'emote emote-still' : 'emote';
  node.style.setProperty('--row', String(emote.row));
  if (emote.ms) node.style.setProperty('--emote-ms', `${emote.ms}ms`);
  // Read out as the shortcode, which is the only name it has.
  node.setAttribute('role', 'img');
  node.setAttribute('aria-label', `:${codeFor(emote)}:`);
  return node;
}

/**
 * Hands the stylesheet the shape of the sheet, so adding an emote never means
 * editing a number in the CSS as well.
 */
export function applyEmoteMetrics() {
  const root = document.documentElement;
  // The sheet's own count, not the combined one: imported packs are not rows in
  // it, and counting them here would rescale the background and shear every
  // hand-drawn emote off its row.
  root.style.setProperty('--emote-rows', String(SHEET_EMOTES.length));
  root.style.setProperty('--emote-frames', String(FRAMES));
  root.style.setProperty('--emote-tile', `${TILE}px`);
}

export { EMOTES };
export default { splitEmotes, emoteNode, emoteFor, codeFor, onlyEmotes, applyEmoteMetrics, EMOTES };
